/**
 * ResearchLinksCard (RESEARCH-LINKS-001)
 * Auto-resolved jurisdiction research links for an order. Used on the
 * Search Fulfillment page (primary) and, in compact mode, in the Order
 * Summary popup. Read-only for processors; manual jurisdiction confirm
 * appears ONLY when auto-resolution fails (ambiguous / not found).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Link2, ExternalLink, Loader2, MapPin, AlertTriangle, Send, X, Clock,
  ScrollText, Home, Map, Landmark, Gavel, Scale, FileSearch, ChevronDown, ChevronRight,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  recorder_deeds: ScrollText,
  assessor: Home,
  gis_parcel: Map,
  tax_portal: Landmark,
  court_judgment: Gavel,
  probate: Scale,
  ucc_state: FileSearch,
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const LinkPill = ({ link }) => {
  const Icon = CATEGORY_ICONS[link.category_key] || Link2;
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      title={`${link.label}${link.notes ? ` — ${link.notes}` : ''}\n${link.url}`}
      className="group inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full border border-blue-100 bg-blue-50/70 hover:bg-blue-600 hover:border-blue-600 transition-colors max-w-full"
      data-testid={`research-link-pill-${link.category_key || 'custom'}-${link.id}`}
    >
      <Icon className="w-3 h-3 text-blue-600 group-hover:text-white flex-shrink-0 transition-colors" />
      <span className="text-[11.5px] font-semibold text-blue-700 group-hover:text-white truncate transition-colors">{link.label}</span>
      {link.level === 'municipality' && (
        <span className="text-[8.5px] font-bold uppercase tracking-wide px-1 rounded bg-indigo-100 text-indigo-600 group-hover:bg-white/20 group-hover:text-white flex-shrink-0" title={link.overrides_county ? 'Municipality link (overrides county)' : 'Municipality link'}>
          Muni
        </span>
      )}
      <ExternalLink className="w-2.5 h-2.5 text-blue-300 group-hover:text-white flex-shrink-0 transition-colors" />
    </a>
  );
};

// Manual jurisdiction confirm — shown ONLY for ambiguous / not_found.
const JurisdictionConfirm = ({ data, onConfirm }) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState(data?.candidates || []);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!query || query.length < 2) return;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data: res } = await axios.get(`${API}/api/jurisdictions/search/suggest`, {
          params: { name: query, state: data?.property?.state || undefined },
          headers: authHeaders(),
        });
        const counties = (res.results || res.suggestions || []).filter(
          (j) => /^(county|parish)$/i.test(j.jurisdiction_type || '')
        );
        setOptions(counties.slice(0, 6));
      } catch { /* silent */ } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, data?.property?.state]);

  return (
    <div className="space-y-2" data-testid="research-jurisdiction-confirm">
      <div className="flex items-center gap-1.5 text-[11.5px] text-amber-700 font-semibold">
        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
        {data.resolution === 'ambiguous' ? 'Multiple county matches — confirm the jurisdiction' : 'County not recognized — confirm the jurisdiction'}
      </div>
      <div className="relative">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search counties${data?.property?.state ? ` in ${data.property.state}` : ''}…`}
          className="h-8 text-[12px]"
          data-testid="research-confirm-search-input"
        />
        {searching && <Loader2 className="w-3.5 h-3.5 animate-spin absolute right-2.5 top-2.5 text-slate-400" />}
      </div>
      {options.length > 0 && (
        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 bg-white overflow-hidden">
          {options.map((j) => (
            <button
              key={j.id}
              type="button"
              onClick={() => onConfirm(j.id)}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-blue-50 transition-colors"
              data-testid={`research-confirm-option-${j.id}`}
            >
              <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
              <span className="text-[12px] font-medium text-slate-700 truncate">{j.jurisdiction_name}</span>
              <span className="text-[10.5px] text-slate-400 ml-auto flex-shrink-0">{j.state}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function ResearchLinksCard({ orderId, compact = false, readOnly = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);
  const [acting, setActing] = useState(false);
  const [collapsed, setCollapsed] = useState(compact);
  const [loadError, setLoadError] = useState(false);

  const fetchLinks = useCallback(async () => {
    if (!orderId) return;
    setLoadError(false);
    try {
      const { data: res } = await axios.get(`${API}/api/orders/${orderId}/research-links`, { headers: authHeaders() });
      setData(res);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403 || status === 404) setHidden(true); // no access — hide silently
      else setLoadError(true); // transient failure — offer retry
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const confirmJurisdiction = async (jurisdictionId) => {
    setActing(true);
    try {
      const { data: res } = await axios.put(
        `${API}/api/orders/${orderId}/research-links/override`,
        { jurisdiction_id: jurisdictionId },
        { headers: authHeaders() },
      );
      setData(res);
      toast.success('Jurisdiction confirmed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to confirm jurisdiction');
    } finally {
      setActing(false);
    }
  };

  const clearOverride = async () => {
    setActing(true);
    try {
      const { data: res } = await axios.delete(`${API}/api/orders/${orderId}/research-links/override`, { headers: authHeaders() });
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear');
    } finally {
      setActing(false);
    }
  };

  const requestLinks = async () => {
    setActing(true);
    try {
      const { data: res } = await axios.post(`${API}/api/orders/${orderId}/research-links/request`, {}, { headers: authHeaders() });
      toast.success(res.already_requested ? 'Already requested — the Jurisdiction team is on it' : 'Research links requested');
      await fetchLinks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit request');
    } finally {
      setActing(false);
    }
  };

  if (hidden || !orderId) return null;

  const resolved = data?.resolution === 'resolved';
  const recognized = data?.resolution === 'recognized'; // ONLINE-COVERAGE-UAT-003 B — real county (US county master), no jurisdiction record yet
  const jurLabel = resolved
    ? [data.county?.jurisdiction_name, data.municipality?.jurisdiction_name && `· ${data.municipality.jurisdiction_name}`].filter(Boolean).join(' ')
    : recognized ? (data.county_master?.full_name || data.county_master?.county_canonical) : null;

  return (
    <div className="rounded-xl border border-slate-200/80 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden" data-testid="research-links-card">
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center gap-1.5 text-left"
        data-testid="research-links-card-header"
      >
        <Link2 className="h-3 w-3 text-blue-600 flex-shrink-0" />
        <h3 className="sf-heading text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">Research Links</h3>
        {(resolved || recognized) && jurLabel && (
          <span
            className="text-[10.5px] text-slate-400 font-medium truncate ml-1"
            title={`${jurLabel}${data.property?.state ? `, ${data.property.state}` : ''}`}
            data-testid="research-links-jurisdiction-label"
          >
            {jurLabel}{data.property?.state ? `, ${data.property.state}` : ''}
          </span>
        )}
        {data?.override_active && (
          <span className="text-[8.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 border border-indigo-100 flex-shrink-0" data-testid="research-links-manual-badge">Manual</span>
        )}
        <span className="ml-auto flex-shrink-0 text-slate-300">
          {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </span>
      </button>

      {!collapsed && (
        <div className="px-4 py-3">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 text-[12px]" data-testid="research-links-loading">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Resolving jurisdiction…
            </div>
          ) : loadError ? (
            <button
              type="button"
              onClick={() => { setLoading(true); fetchLinks(); }}
              className="text-[12px] text-slate-400 hover:text-blue-600 transition-colors"
              data-testid="research-links-retry"
            >
              Couldn't load research links — tap to retry
            </button>
          ) : resolved && data.links_configured ? (
            <div className="flex flex-wrap gap-1.5" data-testid="research-links-pills">
              {data.links.map((l) => <LinkPill key={l.id} link={l} />)}
              {data.override_active && !readOnly && (
                <button
                  type="button"
                  onClick={clearOverride}
                  disabled={acting}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10.5px] text-slate-400 hover:text-rose-500 transition-colors"
                  title="Clear manual jurisdiction selection"
                  data-testid="research-links-clear-override"
                >
                  <X className="w-3 h-3" /> Reset
                </button>
              )}
            </div>
          ) : resolved && !data.links_configured ? (
            <div data-testid="research-links-not-configured">
              {data.pending_request ? (
                <div className="flex items-center gap-2 text-[12px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2" data-testid="research-links-pending-request">
                  <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>
                    Research links requested{data.pending_request.submitted_by_name ? ` by ${data.pending_request.submitted_by_name}` : ''} — pending with the Jurisdiction team.
                  </span>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2 bg-amber-50/70 border border-amber-100 rounded-lg px-3 py-2">
                  <span className="text-[12px] text-amber-800 font-medium">Research links not configured for this jurisdiction.</span>
                  {!readOnly && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={requestLinks}
                      disabled={acting}
                      className="h-7 text-[11.5px] border-amber-300 text-amber-800 hover:bg-amber-100 flex-shrink-0"
                      data-testid="request-research-links-btn"
                    >
                      {acting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                      Request Research Links
                    </Button>
                  )}
                </div>
              )}
              {data.override_active && !readOnly && (
                <button type="button" onClick={clearOverride} disabled={acting} className="mt-1.5 inline-flex items-center gap-1 text-[10.5px] text-slate-400 hover:text-rose-500" data-testid="research-links-clear-override">
                  <X className="w-3 h-3" /> Reset manual jurisdiction
                </button>
              )}
            </div>
          ) : recognized ? (
            <div className="flex items-start gap-2 text-[12px] text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2" data-testid="research-links-recognized">
              <MapPin className="w-3.5 h-3.5 mt-0.5 text-slate-400 flex-shrink-0" />
              <span>
                <span className="font-medium text-slate-700">{data.county_master?.full_name || data.county_master?.county_canonical}, {data.county_master?.state}</span> recognized (US county master{data.county_master?.fips ? ` · FIPS ${data.county_master.fips}` : ''}) — no research links are configured for this county yet.
              </span>
            </div>
          ) : readOnly ? (
            <p className="text-[12px] text-slate-400" data-testid="research-links-unresolved-readonly">
              Jurisdiction not recognized — research links unavailable.
            </p>
          ) : (
            <JurisdictionConfirm data={data || {}} onConfirm={confirmJurisdiction} />
          )}
        </div>
      )}
    </div>
  );
}
